import { useState, useCallback, useMemo } from "react";
import {
  Bug,
  AlertTriangle,
  CheckCircle,
  Eye,
  Search,
  RefreshCw,
  XCircle,
  Clock,
  TrendingUp,
  Shield,
  Globe,
  Wifi,
  Zap,
  CreditCard,
  Lock,
  Code,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import type { FilterConfig } from "@/components/admin/shared/FilterBar";
import {
  useStorefrontErrors,
  useUpdateError,
  useBulkUpdateErrors,
  useErrorStats,
} from "@/hooks/admin/useErrors";
import type {
  StorefrontError,
  ErrorSeverity,
  ErrorCategory,
  ErrorStatus,
  UpdateErrorPayload,
} from "@/lib/admin-api/errors";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_LABELS: Record<ErrorSeverity, string> = {
  critical: "Critique",
  high: "Haute",
  medium: "Moyenne",
  low: "Basse",
};

const SEVERITY_COLORS: Record<ErrorSeverity, string> = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-stone-100 text-stone-600 border-stone-200",
};

const SEVERITY_DOT: Record<ErrorSeverity, string> = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-amber-500",
  low: "bg-stone-400",
};

const CATEGORY_LABELS: Record<ErrorCategory, string> = {
  api_error: "Erreur API",
  network_error: "Réseau",
  rate_limit: "Rate limit",
  js_error: "JavaScript",
  render_error: "Rendu",
  payment_error: "Paiement",
  auth_error: "Authentification",
};

const CATEGORY_ICONS: Record<ErrorCategory, typeof Bug> = {
  api_error: Globe,
  network_error: Wifi,
  rate_limit: Zap,
  js_error: Code,
  render_error: Bug,
  payment_error: CreditCard,
  auth_error: Lock,
};

const STATUS_LABELS: Record<ErrorStatus, string> = {
  new: "Nouveau",
  acknowledged: "Pris en compte",
  investigating: "En investigation",
  resolved: "Résolu",
  ignored: "Ignoré",
};

const STATUS_COLORS: Record<ErrorStatus, string> = {
  new: "bg-red-100 text-red-800 border-red-200",
  acknowledged: "bg-blue-100 text-blue-800 border-blue-200",
  investigating: "bg-amber-100 text-amber-800 border-amber-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  ignored: "bg-stone-100 text-stone-500 border-stone-200",
};

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "severity",
    label: "Sévérité",
    type: "select",
    options: [
      { value: "critical", label: "Critique" },
      { value: "high", label: "Haute" },
      { value: "medium", label: "Moyenne" },
      { value: "low", label: "Basse" },
    ],
  },
  {
    key: "category",
    label: "Catégorie",
    type: "select",
    options: [
      { value: "api_error", label: "Erreur API" },
      { value: "network_error", label: "Réseau" },
      { value: "js_error", label: "JavaScript" },
      { value: "payment_error", label: "Paiement" },
      { value: "auth_error", label: "Authentification" },
      { value: "render_error", label: "Rendu" },
      { value: "rate_limit", label: "Rate limit" },
    ],
  },
  {
    key: "status",
    label: "Statut",
    type: "select",
    options: [
      { value: "new", label: "Nouveau" },
      { value: "acknowledged", label: "Pris en compte" },
      { value: "investigating", label: "En investigation" },
      { value: "resolved", label: "Résolu" },
      { value: "ignored", label: "Ignoré" },
    ],
  },
  { key: "date_from", label: "Du", type: "date" },
  { key: "date_to", label: "Au", type: "date" },
];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatDateTime(d: string | null): string {
  if (!d) return "\u2014";
  return new Date(d).toLocaleString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `Il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

// ---------------------------------------------------------------------------
// Error detail modal
// ---------------------------------------------------------------------------

interface ErrorDetailModalProps {
  readonly error: StorefrontError | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onStatusChange: (id: string, payload: UpdateErrorPayload) => void;
  readonly isUpdating: boolean;
}

function ErrorDetailModal({
  error,
  open,
  onClose,
  onStatusChange,
  isUpdating,
}: ErrorDetailModalProps) {
  const [notes, setNotes] = useState("");

  if (!error) return null;

  const CategoryIcon = CATEGORY_ICONS[error.category];

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Détail de l'erreur"
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between">
          <div className="flex gap-2">
            {error.status !== "resolved" && (
              <Button
                variant="default"
                onClick={() =>
                  onStatusChange(error.id, { status: "resolved", notes: notes || undefined })
                }
                disabled={isUpdating}
                className="gap-1.5"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Résoudre
              </Button>
            )}
            {error.status === "new" && (
              <Button
                variant="outline"
                onClick={() =>
                  onStatusChange(error.id, { status: "acknowledged" })
                }
                disabled={isUpdating}
              >
                Prendre en compte
              </Button>
            )}
            {error.status !== "ignored" && error.status !== "resolved" && (
              <Button
                variant="ghost"
                onClick={() =>
                  onStatusChange(error.id, { status: "ignored", notes: notes || undefined })
                }
                disabled={isUpdating}
              >
                Ignorer
              </Button>
            )}
          </div>
          <Button variant="ghost" onClick={onClose}>
            Fermer
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Header badges */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${SEVERITY_COLORS[error.severity]}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${SEVERITY_DOT[error.severity]}`} />
            {SEVERITY_LABELS[error.severity]}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[error.status]}`}
          >
            {STATUS_LABELS[error.status]}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-xs font-medium text-stone-600">
            <CategoryIcon className="h-3 w-3" />
            {CATEGORY_LABELS[error.category]}
          </span>
        </div>

        {/* Error message */}
        <div>
          <h3 className="text-sm font-medium text-stone-500">Message</h3>
          <p className="mt-1 rounded-md bg-red-50 px-3 py-2 font-mono text-sm text-red-800">
            {error.message}
          </p>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs text-stone-500">Page</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">{error.url}</p>
          </div>
          <div>
            <p className="text-xs text-stone-500">Statut HTTP</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">
              {error.http_status ?? "\u2014"}
            </p>
          </div>
          <div>
            <p className="text-xs text-stone-500">Occurrences</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">
              {error.occurrence_count}
            </p>
          </div>
          <div>
            <p className="text-xs text-stone-500">Première apparition</p>
            <p className="mt-0.5 text-sm text-stone-700">
              {formatDateTime(error.first_seen_at)}
            </p>
          </div>
          <div>
            <p className="text-xs text-stone-500">Dernière apparition</p>
            <p className="mt-0.5 text-sm text-stone-700">
              {formatDateTime(error.last_seen_at)}
            </p>
          </div>
          <div>
            <p className="text-xs text-stone-500">Source</p>
            <p className="mt-0.5 text-sm font-medium text-stone-900">{error.source}</p>
          </div>
        </div>

        {/* Stack trace */}
        {error.stack_trace && (
          <div>
            <h3 className="text-sm font-medium text-stone-500">Stack trace</h3>
            <pre className="mt-1 max-h-48 overflow-auto rounded-md bg-stone-900 p-3 text-xs text-stone-200">
              {error.stack_trace}
            </pre>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Notes de résolution
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Décrivez la cause et la résolution\u2026"
          />
        </div>

        {/* Existing notes */}
        {error.notes && (
          <div>
            <h3 className="text-sm font-medium text-stone-500">Notes précédentes</h3>
            <p className="mt-1 rounded-md bg-stone-50 px-3 py-2 text-sm text-stone-700">
              {error.notes}
            </p>
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ErrorDashboard() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedError, setSelectedError] = useState<StorefrontError | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const params = useMemo(
    () => ({
      page,
      limit: 25,
      severity: (filters.severity as ErrorSeverity) ?? undefined,
      category: (filters.category as ErrorCategory) ?? undefined,
      status: (filters.status as ErrorStatus) ?? undefined,
      date_from: (filters.date_from as string) ?? undefined,
      date_to: (filters.date_to as string) ?? undefined,
      q: searchQuery || undefined,
    }),
    [page, filters, searchQuery],
  );

  const { data, isLoading, refetch } = useStorefrontErrors(params);
  const { data: statsData } = useErrorStats();
  const updateMutation = useUpdateError();
  const bulkMutation = useBulkUpdateErrors();

  const errors = data?.errors ?? [];
  const pagination = data?.pagination;
  const stats = statsData ?? data?.stats;

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleStatusChange = useCallback(
    (id: string, payload: UpdateErrorPayload) => {
      updateMutation.mutate(
        { id, payload },
        {
          onSuccess: () => setSelectedError(null),
        },
      );
    },
    [updateMutation],
  );

  const handleBulkResolve = useCallback(() => {
    if (selectedIds.size === 0) return;
    bulkMutation.mutate(
      { ids: Array.from(selectedIds), payload: { status: "resolved" } },
      {
        onSuccess: () => setSelectedIds(new Set()),
      },
    );
  }, [selectedIds, bulkMutation]);

  const handleBulkIgnore = useCallback(() => {
    if (selectedIds.size === 0) return;
    bulkMutation.mutate(
      { ids: Array.from(selectedIds), payload: { status: "ignored" } },
      {
        onSuccess: () => setSelectedIds(new Set()),
      },
    );
  }, [selectedIds, bulkMutation]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === errors.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(errors.map((e) => e.id)));
    }
  }, [selectedIds.size, errors]);

  const columns: readonly ColumnDef<StorefrontError>[] = [
    {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          checked={selectedIds.size > 0 && selectedIds.size === errors.length}
          onChange={toggleSelectAll}
          className="h-4 w-4 rounded border-stone-300"
        />
      ),
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          className="h-4 w-4 rounded border-stone-300"
        />
      ),
    },
    {
      id: "severity",
      header: "",
      cell: (row) => (
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${SEVERITY_DOT[row.severity]}`}
          title={SEVERITY_LABELS[row.severity]}
        />
      ),
    },
    {
      id: "message",
      header: "Erreur",
      cell: (row) => {
        const CategoryIcon = CATEGORY_ICONS[row.category];
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <CategoryIcon className="h-3.5 w-3.5 shrink-0 text-stone-400" />
              <span className="truncate text-sm font-medium text-stone-900">
                {row.message}
              </span>
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-stone-400">
              <span>{row.url}</span>
              {row.http_status && (
                <span className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-stone-600">
                  {row.http_status}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: "category",
      header: "Type",
      cell: (row) => (
        <span className="text-xs text-stone-600">
          {CATEGORY_LABELS[row.category]}
        </span>
      ),
    },
    {
      id: "occurrences",
      header: "Occurrences",
      cell: (row) => (
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-700">
          {row.occurrence_count}
        </span>
      ),
    },
    {
      id: "status",
      header: "Statut",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[row.status]}`}
        >
          {STATUS_LABELS[row.status]}
        </span>
      ),
    },
    {
      id: "last_seen",
      header: "Dernière fois",
      cell: (row) => (
        <span className="text-xs text-stone-500" title={formatDateTime(row.last_seen_at)}>
          {timeAgo(row.last_seen_at)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (row) => (
        <button
          onClick={() => setSelectedError(row)}
          className="flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-50"
          title="Voir les détails"
        >
          <Eye className="h-3.5 w-3.5" />
          Détails
        </button>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600">
              <Bug className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Erreurs Storefront
              </h1>
              <p className="text-sm text-stone-500">
                Suivi en temps réel des erreurs côté client sur djayma.com
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => refetch()} className="gap-1.5">
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <KPICard
            label="Total erreurs"
            value={stats?.total ?? 0}
            icon={<Bug className="h-4 w-4 text-stone-500" />}
          />
          <KPICard
            label="Nouvelles"
            value={stats?.new_count ?? 0}
            icon={<AlertTriangle className="h-4 w-4 text-red-500" />}
          />
          <KPICard
            label="Critiques"
            value={stats?.critical_count ?? 0}
            icon={<XCircle className="h-4 w-4 text-red-600" />}
          />
          <KPICard
            label="Aujourd'hui"
            value={stats?.today_count ?? 0}
            icon={<Clock className="h-4 w-4 text-amber-500" />}
          />
          <KPICard
            label="Résolues aujourd'hui"
            value={stats?.resolved_today ?? 0}
            icon={<CheckCircle className="h-4 w-4 text-emerald-500" />}
          />
        </div>

        {/* Live alert banner */}
        {(stats?.critical_count ?? 0) > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <Shield className="h-5 w-5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">
                {stats?.critical_count} erreur(s) critique(s) non résolue(s)
              </p>
              <p className="text-xs text-red-600">
                Les erreurs critiques affectent les paiements ou l'authentification des clients.
                Résolvez-les en priorité.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                handleFilterChange("severity", "critical");
                handleFilterChange("status", "new");
              }}
              className="shrink-0 border-red-200 text-red-700 hover:bg-red-100"
            >
              Voir les critiques
            </Button>
          </div>
        )}

        {/* Search + Filters */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Rechercher par message, URL, catégorie\u2026"
              className="w-full rounded-lg border border-stone-200 bg-white py-2 pl-9 pr-4 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <FilterBar
              filters={FILTER_CONFIGS}
              values={filters}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.size} erreur(s) sélectionnée(s)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkResolve}
                disabled={bulkMutation.isPending}
                className="gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Résoudre
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBulkIgnore}
                disabled={bulkMutation.isPending}
                className="text-stone-500"
              >
                Ignorer
              </Button>
            </div>
          </div>
        )}

        {/* Error table */}
        <AdminDataTable
          columns={columns}
          data={errors}
          pagination={
            pagination
              ? { page: pagination.page, limit: pagination.limit, total: pagination.total }
              : undefined
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="Aucune erreur trouvée. Le storefront fonctionne normalement."
        />

        {/* Trend hint */}
        {(stats as unknown as { trend?: readonly { date: string; count: number }[] })?.trend &&
          ((stats as unknown as { trend: readonly { date: string; count: number }[] }).trend.length > 0) && (
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-stone-500" />
              <h3 className="text-sm font-medium text-stone-700">Tendance (7 derniers jours)</h3>
            </div>
            <div className="flex items-end gap-1 h-20">
              {(stats as unknown as { trend: readonly { date: string; count: number }[] }).trend.map(
                (point) => {
                  const maxCount = Math.max(
                    ...(stats as unknown as { trend: readonly { date: string; count: number }[] }).trend.map(
                      (p) => p.count,
                    ),
                  );
                  const height = maxCount > 0 ? (point.count / maxCount) * 100 : 0;
                  return (
                    <div key={point.date} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-stone-400">{point.count}</span>
                      <div
                        className="w-full rounded-sm bg-red-200"
                        style={{ height: `${Math.max(height, 4)}%` }}
                      />
                      <span className="text-[10px] text-stone-400">
                        {new Date(point.date).toLocaleDateString("fr-SN", {
                          day: "2-digit",
                          month: "2-digit",
                        })}
                      </span>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error detail modal */}
      <ErrorDetailModal
        error={selectedError}
        open={selectedError !== null}
        onClose={() => setSelectedError(null)}
        onStatusChange={handleStatusChange}
        isUpdating={updateMutation.isPending}
      />
    </AdminLayout>
  );
}
